import { differenceWith, isEqual } from 'lodash';
import moment from 'moment-timezone';
import { getTimezone } from '@mgm/common/utils';
import {
  TEmptySubmission, TPersonnel,
  TSubmission,
  TSubmissionTask,
  TTasklist,
} from '../../../../types';

const getFormattedSubmissions = (submissions: { [key: string]: Array<TEmptySubmission> }) => {
  const formattedSubmissions: Array<TEmptySubmission> = [];

  Object.values(submissions).forEach((value) => {
    formattedSubmissions.push(...value);
  });

  return formattedSubmissions;
};

const getFormattedTasks = (
  tasks: Array<TSubmissionTask>,
  email: string,
): Array<TSubmissionTask> => tasks?.reduce((acc: Array<TSubmissionTask>, task: TSubmissionTask) => {
  const formattedTask = {
    ...task,
    isDone: task.doneBy?.some((doneBy) => doneBy.email === email),
  };

  acc.push(formattedTask);

  return acc;
}, []);

const getTasklistsToSubmit = (
  tasklists: Array<TTasklist>,
  dailySubmissions: Array<TSubmission>,
) => tasklists.filter((tasklist) => !dailySubmissions.some((submission) => tasklist.id === submission.tasklistId));

const isTasksChange = (
  newTasks: Array<TSubmissionTask>,
  oldTasks: Array<TSubmissionTask>,
) => {
  const localOldTasks = oldTasks.map((task) => ({
    ...task,
    isDone: task.isDone || false,
  }));

  const localNewTask = newTasks.map((task) => ({
    ...task,
    isDone: task.isDone || false,
  }));

  return differenceWith(localNewTask, localOldTasks, isEqual);
};

const getTasklistToSubmitByFilters = (
  tasklists: Array<TTasklist>,
  user: TPersonnel,
) => tasklists.filter((tasklist) => {
  const isAdmin = user.permissionLevel === 'Admin';
  const isUserAssignee = tasklist.assigneeEmail === user.email || !tasklist.assigneeEmail;

  const isDated = tasklist.isDated
    ? tasklist.date && moment.tz(tasklist.date, getTimezone()).isSame(moment.tz(getTimezone()), 'day') : true;

  const localVisibility = tasklist.visibility.map((visibility) => ({
    name: visibility,
    id: visibility,
  }));

  const permissions = localVisibility?.map((visibility) => visibility.id) as unknown as Array<string>;
  const isPermission = isAdmin ? true : permissions?.includes(user.permissionLevel?.toLowerCase());

  return isPermission && isUserAssignee && isDated;
});

export {
  getFormattedSubmissions,
  getFormattedTasks,
  getTasklistsToSubmit,
  isTasksChange,
  getTasklistToSubmitByFilters,
};
